import React, { useState, useEffect, useRef, useMemo } from "react";
import { CSVLink } from "react-csv";
import Select from "react-select";
import Datepicker from "react-tailwindcss-datepicker";
import { SlDrawer, SlButton } from "@shoelace-style/shoelace/dist/react";
import API from "../../services/api";
import TableLoader from "../../shared/TableLoader";
import NoDataFound from "../../shared/NoDataFound";
import MultiSelectFilter from "../../shared/MultiSelectFilter";
import { langoptions, toLakhs } from "../../utils/constants";

const todayRange = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return { startDate: d, endDate: d };
};

const STATUSES = ["pending", "live", "completed"];

const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-700",
  live: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
};

const HOVER = {
  pending: "cursor-pointer hover:bg-yellow-50",
  live: "cursor-pointer hover:bg-blue-50",
  completed: "cursor-pointer hover:bg-green-50",
};

function CreativeReportDesignerAssigned({ initiateDownloadCSV, setInitiateDownloadCSV }) {
  const [reportData, setReportData] = useState({});
  const [loader, setLoader] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);

  const [dateRange, setDateRange] = useState(todayRange);
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRow, setDrawerRow] = useState(null);
  const [drawerCat, setDrawerCat] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    const allLangIds = langoptions.map((l) => l.value);
    API({ url: "/location/getStatesByLangId", method: "POST", responseType: "json", data: allLangIds })
      .then((res) => {
        if (Array.isArray(res.data))
          setStateOptions(res.data.map((s) => ({ value: s.state_id, label: s.state_name, lang_id: s.lang_id })));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedState) { setDistrictOptions([]); setSelectedDistrict(null); return; }
    setSelectedDistrict(null);
    API({ url: "/location/getDistricts", method: "POST", responseType: "json", data: { lang_id: selectedState.lang_id, state_id: selectedState.value } })
      .then((res) => {
        if (res.code === 200)
          setDistrictOptions(res.data.map((d) => ({ value: d.dist_id, label: d.dist_name })));
      })
      .catch(console.error);
  }, [selectedState]);

  const fetchData = async () => {
    try {
      setLoader(true);
      const res = await API({
        url: "/alpha_cst_designer/creativeReportsDesignerAssigned",
        method: "POST",
        responseType: "json",
        data: {
          startDate: dateRange?.startDate || null,
          endDate: dateRange?.endDate || null,
          state_id: selectedState?.value || null,
          district_id: selectedDistrict?.value || null,
        },
      });
      setReportData(res?.code === 200 ? res.data || {} : {});
    } catch (err) {
      console.error(err);
      setReportData({});
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { fetchData(); }, [dateRange, selectedState, selectedDistrict]);

  // reportData: { [state_id]: { [dist_id]: { pending?: { state_name, dist_name, data[] }, live?: ..., completed?: ... } } }
  const rows = useMemo(() => {
    const result = [];
    Object.entries(reportData).forEach(([stateId, distMap]) => {
      Object.entries(distMap).forEach(([distId, statusMap]) => {
        const first = Object.values(statusMap)[0] || {};
        const byStatus = Object.fromEntries(STATUSES.map((s) => [s, statusMap[s]?.data || []]));
        const counts = Object.fromEntries(STATUSES.map((s) => [s, byStatus[s].length]));
        result.push({
          key: `${stateId}-${distId}`,
          state_name: first.state_name || "",
          dist_name: first.dist_name || "",
          ...counts,
          total: STATUSES.reduce((sum, s) => sum + counts[s], 0),
          byStatus,
        });
      });
    });
    return result;
  }, [reportData]);

  const visibleRows = useMemo(
    () => (selectedRowKeys.length ? rows.filter((r) => selectedRowKeys.includes(r.key)) : rows),
    [rows, selectedRowKeys]
  );

  const totals = useMemo(
    () => visibleRows.reduce(
      (acc, row) => { STATUSES.forEach((s) => { acc[s] += row[s]; }); acc.total += row.total; return acc; },
      Object.fromEntries([...STATUSES.map((s) => [s, 0]), ["total", 0]])
    ),
    [visibleRows]
  );

  useEffect(() => {
    if (!initiateDownloadCSV) return;
    // CSV always covers every state/district, independent of the on-screen row filter.
    const csvTotals = rows.reduce(
      (acc, row) => { STATUSES.forEach((s) => { acc[s] += row[s]; }); acc.total += row.total; return acc; },
      Object.fromEntries([...STATUSES.map((s) => [s, 0]), ["total", 0]])
    );
    setCsvData([
      ["State", "District", "Pending", "Live", "Completed", "Total"],
      ...rows.map((r) => [r.state_name, r.dist_name, r.pending, r.live, r.completed, r.total]),
      ["TOTAL", "", csvTotals.pending, csvTotals.live, csvTotals.completed, csvTotals.total],
    ]);
    setTimeout(() => { csvLinkRef.current?.link?.click(); setInitiateDownloadCSV(false); }, 100);
  }, [initiateDownloadCSV]);

  const openDrawer = (row, cat) => { setDrawerRow(row); setDrawerCat(cat); setDrawerOpen(true); };

  return (
    <div>
      <div className="flex flex-wrap gap-4 mb-4 items-end">
        <div className="w-64">
          <label className="block text-xs font-semibold mb-1">Date Range</label>
          <div className="border rounded-lg border-gray-300 hover:border-2 hover:border-sky-500">
            <Datepicker value={dateRange} onChange={(val) => setDateRange(val ?? null)} primaryColor="cyan" showShortcuts maxDate={new Date()} popoverDirection="down" />
          </div>
        </div>
        <div className="w-64">
          <label className="block text-xs font-semibold mb-1">State</label>
          <Select options={stateOptions} value={selectedState} onChange={setSelectedState} isClearable placeholder="Select State" classNamePrefix="react-select" />
        </div>
        {selectedState && (
          <div className="w-64">
            <label className="block text-xs font-semibold mb-1">District</label>
            <Select options={districtOptions} value={selectedDistrict} onChange={setSelectedDistrict} isClearable placeholder="Select District" classNamePrefix="react-select" />
          </div>
        )}
      </div>

      <CSVLink ref={csvLinkRef} data={csvData} filename="creative_report_designer_assigned.csv" style={{ display: "none" }} />

      <MultiSelectFilter
        label="State / District"
        options={rows.map((r) => ({ value: r.key, label: `${r.state_name} - ${r.dist_name}` }))}
        selected={selectedRowKeys}
        onChange={setSelectedRowKeys}
      />

      <div className="overflow-x-auto">
        <table className="table table-bordered">
          <thead className="sticky top-0">
            <tr>
              <th>State</th>
              <th>District</th>
              <th>Pending</th>
              <th>Live</th>
              <th>Completed</th>
              <th>Total</th>
            </tr>
          </thead>
          {loader ? (
            <TableLoader colspan={6} />
          ) : visibleRows.length === 0 ? (
            <NoDataFound colspan={6} />
          ) : (
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.key}>
                  <td>{row.state_name}</td>
                  <td>{row.dist_name}</td>
                  {STATUSES.map((s) => (
                    <td key={s} className={row[s] ? HOVER[s] : ""} onClick={() => row[s] && openDrawer(row, s)}>
                      {row[s]}
                    </td>
                  ))}
                  <td>{row.total}</td>
                </tr>
              ))}
              <tr className="font-bold bg-gray-100 pointer-events-none">
                <td>TOTAL</td>
                <td></td>
                <td>{totals.pending}</td>
                <td>{totals.live}</td>
                <td>{totals.completed}</td>
                <td>{totals.total}</td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <SlDrawer
        label={drawerRow && drawerCat ? `${drawerCat.charAt(0).toUpperCase() + drawerCat.slice(1)} — ${drawerRow.dist_name}, ${drawerRow.state_name}` : "Creatives"}
        open={drawerOpen}
        onSlAfterHide={() => setDrawerOpen(false)}
        style={{ "--size": window.innerWidth <= 767 ? "100vw" : "55vw" }}
      >
        {drawerRow && drawerCat && (() => {
          const data = drawerRow.byStatus[drawerCat];
          return (
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 capitalize ${STATUS_STYLES[drawerCat]}`}>
                {drawerCat} ({data.length})
              </span>
              {data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="table table-bordered text-sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Creative Name</th>
                        <th>Client</th>
                        <th>Ad Type</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((c, idx) => (
                        <tr key={c.creative_id}>
                          <td>{idx + 1}</td>
                          <td className="max-w-xs truncate" title={c.creative_name}>{c.creative_name}</td>
                          <td title={c.client_name}>{c.client_name}</td>
                          <td>{c.ad_creative_sub_type || "-"}</td>
                          <td title={`₹${parseFloat(c.amount || 0).toLocaleString("en-IN")}`}>{toLakhs(parseFloat(c.amount || 0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No detail data available</p>
              )}
            </div>
          );
        })()}
        <SlButton slot="footer" variant="primary" onClick={() => setDrawerOpen(false)}>Close</SlButton>
      </SlDrawer>
    </div>
  );
}

export default CreativeReportDesignerAssigned;
